// Generated by Copilot- Start - 2026-04-21 20:55:55
import { arraytoObj, cleanEmpty } from "../../helper/sb-fixer";
import { ComponentNames } from "../../constant/component-constant";
import { logAdapterEvent, logError } from "../../helper/logger";
import { IGenreSvodEpochOffers, IPackageConfig } from "../../types/global";

const ADAPTER_NAME = "dtvCompetitorTableAdapter";

interface IDtvCompetitorTableAdapterArgs {
  content: any;
  genreSvodOffers?: IGenreSvodEpochOffers;
  packageConfig?: IPackageConfig[];
  cmsPageName?: string;
}

// queryparam (e.g. "mysports") → offer key (e.g. "ATT_TV_MYSPORTS")
const buildQueryParamMap = (pkgs?: IPackageConfig[]): Record<string, string> => pkgs?.reduce((map, pkg: any) => {
  if (pkg?.queryparam && pkg?.id) {
    map[pkg.queryparam.toLowerCase()] = pkg.id;
  }
  return map;
}, {} as Record<string, string>) ?? {};

// array of { "ATT_TV_MYSPORTS": { price, regularPrice } } → flat map
const buildPricesMap = (offerPrices: IGenreSvodEpochOffers["offerPrices"]): Record<string, any> => offerPrices?.reduce((map, entry: any) => {
  const key = Object.keys(entry ?? {})[0];
  if (key) {
    map[key] = entry[key];
  }
  return map;
}, {} as Record<string, any>) ?? {};

// Replace [offer_price] placeholder with the actual price. Returns text unchanged if no placeholder is found or price is unavailable.
const replacePlaceholders = (text: string, prices: { price?: number }): string => {
  if (prices?.price == null) {
    return text;
  }
  if (text?.includes("[offer_price]")) {
    return text.replace("[offer_price]", `${prices.price.toFixed(2)}`);
  }
  return text;
};

// Applies placeholder replacement to a title/description field which may be
// a plain string, a { content: string } object, or an array of such objects.
const replaceInTextField = (field: any, prices: { price?: number }): any => {
  if (!field) {
    return field;
  }
  if (typeof field === "string") {
    return replacePlaceholders(field, prices);
  }
  if (!Array.isArray(field) && typeof field === "object" && typeof field.content === "string") {
    return {
      ...field,
      content: replacePlaceholders(field.content, prices)
    };
  }
  if (Array.isArray(field)) {
    return field.map((item: any) => {
      if (item && typeof item === "object" && typeof item.content === "string") {
        return {
          ...item,
          content: replacePlaceholders(item.content, prices)
        };
      }
      return item;
    });
  }
  return field;
};

// Replace price placeholders in a single directv product's values.
// CMS raw structure: product.values = [{ component:'array_features', features1:[{title,description,...}], features2:[...], ... }]
const injectPricesInProduct = (product: any, pricesMap: Record<string, any>, queryParamMap: Record<string, string>): any => {
  if (product.category !== "directv") {
    return product;
  }

  const rawKey = (product.priceKey ?? product.name ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const offerKey = queryParamMap[rawKey];
  const prices = offerKey ? pricesMap[offerKey] : null;
  if (!prices) {
    return product;
  }

  if (!Array.isArray(product.values)) {
    return product;
  }

  const updatedValues = product.values.map((valObj: any) => {
    if (!valObj || typeof valObj !== "object") {
      return valObj;
    }
    const updatedValObj = { ...valObj };
    for (const [k, featureArr] of Object.entries<any>(valObj)) {
      if (!k.startsWith("features") || !Array.isArray(featureArr)) {
        continue;
      }
      updatedValObj[k] = featureArr.map((feature: any) => {
        if (!feature || typeof feature !== "object") {
          return feature;
        }
        const updatedFeature = { ...feature };
        if (feature.description !== undefined) {
          updatedFeature.description = replaceInTextField(feature.description, prices);
        }
        if (feature.title !== undefined) {
          updatedFeature.title = replaceInTextField(feature.title, prices);
        }
        if (feature.titleDescription !== undefined) {
          updatedFeature.titleDescription = replaceInTextField(feature.titleDescription, prices);
        }
        return updatedFeature;
      });
    }
    return updatedValObj;
  });

  return {
    ...product,
    values: updatedValues
  };
};

// Recursively converts any object with `mobileWidth` or `mobileHeight` (CI-authored
// responsive overrides added to the uf_styles component) into MUI-compatible responsive
// breakpoint values on `width`/`height`. Must be called after arraytoObj/cleanEmpty so
// that the sx arrays have already been collapsed to plain objects.
const applyResponsiveSx = (node: any): any => {
  if (!node || typeof node !== "object") {
    return node;
  }
  if (Array.isArray(node)) {
    return node.map(applyResponsiveSx);
  }
  const result: Record<string, any> = {};
  for (const [k, v] of Object.entries(node)) {
    result[k] = applyResponsiveSx(v);
  }
  if (result.mobileWidth !== undefined || result.mobileHeight !== undefined) {
    if (result.mobileWidth !== undefined) {
      result.width = result.width ? {
        xs: result.mobileWidth,
        md: result.width
      } : { xs: result.mobileWidth };
      delete result.mobileWidth;
    }
    if (result.mobileHeight !== undefined) {
      result.height = result.height ? {
        xs: result.mobileHeight,
        md: result.height
      } : { xs: result.mobileHeight };
      delete result.mobileHeight;
    }
  }
  return result;
};

// Walk all directv product feature cells and replace placeholders in title/description.
// Handles both top-level array_products and products nested inside tabs[i].table (used by DtvCompetitorTableModule).
// Note: operates on raw CMS content before arraytoObj strips the "array_" prefix.
const injectPrices = (content: any, pricesMap: Record<string, any>, queryParamMap: Record<string, string>): any => {
  const updated = structuredClone(content);

  // Top-level products (non-tabs mode) — raw CMS field is "array_products"
  if (Array.isArray(updated.array_products)) {
    updated.array_products = updated.array_products.map((p: any) => injectPricesInProduct(p, pricesMap, queryParamMap));
  }

  // Products nested inside tabs[i].table (DtvCompetitorTableModule spreads tabs[0].table over props)
  const { tabs } = updated;
  const tabsArr = Array.isArray(tabs) ? tabs : tabs != null ? [tabs] : [];
  if (tabsArr.length > 0) {
    const updatedTabs = tabsArr.map((tab: any) => {
      if (!tab?.table) {
        return tab;
      }
      const { table } = tab;
      const productsKey = Array.isArray(table.array_products) ? "array_products" : Array.isArray(table.products) ? "products" : null;
      if (!productsKey) {
        return tab;
      }
      return {
        ...tab,
        table: {
          ...table,
          [productsKey]: table[productsKey].map((p: any) => injectPricesInProduct(p, pricesMap, queryParamMap))
        }
      };
    });
    updated.tabs = Array.isArray(tabs) ? updatedTabs : updatedTabs[0];
  }

  return updated;
};

export const dtvCompetitorTableAdapter = ({
  content,
  genreSvodOffers,
  packageConfig,
  cmsPageName
}: IDtvCompetitorTableAdapterArgs): any => {
  try {
    logAdapterEvent(ADAPTER_NAME, "adapting COMPETITORTABLE", { cmsPageName });

    const pricesMap = buildPricesMap(genreSvodOffers?.offerPrices ?? []);
    const queryParamMap = buildQueryParamMap(packageConfig);
    const updatedContent = injectPrices(content, pricesMap, queryParamMap);

    const fixedContent = applyResponsiveSx(cleanEmpty(arraytoObj(updatedContent)));
    return {
      props: {
        ...fixedContent,
        ...(fixedContent.options && { gnavOptions: fixedContent.options }),
        options: {
          id: fixedContent?.id || null,
          ...(fixedContent.options && { ...fixedContent.options })
        }
      },
      type: ComponentNames.COMPETITORTABLE
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, { cmsPageName });
    return null;
  }
};
// Generated by Copilot- End - 2026-04-21 20:55:55

export default dtvCompetitorTableAdapter;
